/**
 * Utility functions for managing workflow items in the Workflow sheet
 */

/**
 * Technology options for scenarios
 */
export const TECHNOLOGIES = ['neo', 'hopper', 'triad', 'dendro', 'throg'];

/**
 * Resource size options for running scenarios
 */
export const RESOURCE_SIZES = ['mini', '4XS', '3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

/**
 * Adds a workflow item to the Workflow sheet
 * Finds the first empty column in row 1 and writes the keys and values vertically
 *
 * @param {Array<string>} keys - Array of key names (e.g., ["ActionName", "ActionType", "TableName"])
 * @param {Array<string>} values - Array of corresponding values
 * @param {string} workflowSheetName - Name of the workflow sheet (default: "Workflow")
 * @returns {Promise<void>}
 */
export async function addWorkflowItem(keys, values, workflowSheetName = 'Workflow') {
    if (keys.length !== values.length) {
        throw new Error(`Keys and values must have the same length. Got ${keys.length} keys and ${values.length} values.`);
    }

    await window.Excel.run(async (context) => {
        const workbook = context.workbook;

        // === Ensure the workflow sheet exists ===
        let workflowSheet = workbook.worksheets.getItemOrNullObject(workflowSheetName);
        workflowSheet.load("name");
        await context.sync();

        if (workflowSheet.isNullObject) {
            workflowSheet = workbook.worksheets.add(workflowSheetName);
            await context.sync();
        }

        // === Find the first empty column in row 1 ===
        let emptyCol = 0;

        // Try to get the used range to find where data actually exists
        const usedRange = workflowSheet.getUsedRangeOrNullObject();
        usedRange.load("columnCount, values");
        await context.sync();

        if (!usedRange.isNullObject) {
            // If there's data, check each column in row 1 to find the first empty one
            const firstRowValues = usedRange.values[0];

            // Find the first empty cell, or use the column after the last used column
            emptyCol = firstRowValues.findIndex(cell => cell === null || cell === "");

            if (emptyCol === -1) {
                // No empty cells found in used range, append after the last column
                emptyCol = firstRowValues.length;
            }
        }
        // else: usedRange is null (empty sheet), so emptyCol stays at 0 (column A)

        const colLetter = String.fromCharCode(65 + emptyCol); // A=65

        // Write keys and values to adjacent columns
        const keysRange = workflowSheet.getRange(`${colLetter}1:${colLetter}${keys.length}`);
        const valuesRange = workflowSheet.getRange(
            `${String.fromCharCode(65 + emptyCol + 1)}1:${String.fromCharCode(65 + emptyCol + 1)}${values.length}`
        );

        keysRange.values = keys.map(key => [key]);
        valuesRange.values = values.map(val => [val]);

        await context.sync();
    });
}

/**
 * Creates a workflow item data structure for Export Data action
 *
 * @param {string} actionName - The name of the action
 * @param {string} tableName - The Cosmic Frog table name
 * @param {string} sheetName - The Excel sheet name
 * @returns {object} Object with keys and values arrays
 */
export function createExportDataWorkflowItem(actionName, tableName, sheetName) {
    return {
        keys: ["ActionName", "ActionType", "CosmicFrogTable", "SheetName"],
        values: [actionName, "ExportData", tableName, sheetName]
    };
}

/**
 * Creates a workflow item data structure for Create Scenario action
 *
 * @param {string} actionName - The name of the action
 * @param {string} scenarioName - The scenario name
 * @param {string} scenarioDescription - The scenario description
 * @param {string} technology - The technology (neo, hopper, triad, dendro, throg)
 * @returns {object} Object with keys and values arrays
 */
export function createScenarioWorkflowItem(actionName, scenarioName, scenarioDescription, technology) {
    return {
        keys: ["ActionName", "ActionType", "ScenarioName", "ScenarioDescription", "Technology"],
        values: [actionName, "CreateScenario", scenarioName, scenarioDescription, technology]
    };
}

/**
 * Creates a workflow item data structure for Create/Connect Model action
 *
 * @param {string} actionName - The name of the action
 * @param {string} modelName - The model name
 * @param {string} templateName - The template name
 * @returns {object} Object with keys and values arrays
 */
export function createConnectModelWorkflowItem(actionName, modelName, templateName) {
    return {
        keys: ["ActionName", "ActionType", "ModelName", "TemplateName"],
        values: [actionName, "ConnectToOrCreateModel", modelName, templateName]
    };
}

/**
 * Creates a workflow item data structure for Create Scenario Item action
 *
 * @param {string} actionName - The name of the action
 * @param {string} itemName - The item name
 * @param {string} itemDescription - The item description
 * @param {string} tableName - The table name
 * @param {string} action - The action details (multiline)
 * @param {string} condition - The condition details (multiline)
 * @param {string} scenarioName - The scenario name
 * @returns {object} Object with keys and values arrays
 */
export function createScenarioItemWorkflowItem(actionName, itemName, itemDescription, tableName, action, condition, scenarioName) {
    return {
        keys: ["ActionName", "ActionType", "ItemName", "ItemDescription", "TableName", "Action", "Condition", "ScenarioName"],
        values: [actionName, "CreateItem", itemName, itemDescription, tableName, action, condition, scenarioName]
    };
}

/**
 * Creates a workflow item data structure for Run Scenario Model action
 *
 * @param {string} actionName - The name of the action
 * @param {string} scenarioName - The scenario name
 * @param {string} technology - The technology (neo, hopper, triad, dendro, throg)
 * @param {string} resourceSize - The resource size (mini, 4XS, 3XS, 2XS, XS, S, M, L, XL, 2XL, 3XL, 4XL)
 * @returns {object} Object with keys and values arrays
 */
export function createRunScenarioModelWorkflowItem(actionName, scenarioName, technology, resourceSize) {
    return {
        keys: ["ActionName", "ActionType", "ScenarioName", "Technology", "ResourceSize"],
        values: [actionName, "RunScenario", scenarioName, technology, resourceSize]
    };
}

/**
 * Creates a workflow item data structure for Run Utility action
 *
 * @param {string} actionName - The name of the action
 * @param {string} utilityName - The utility name
 * @param {string} utilityParams - The utility parameters (multiline)
 * @returns {object} Object with keys and values arrays
 */
export function createRunUtilityWorkflowItem(actionName, utilityName, utilityParams) {
    return {
        keys: ["ActionName", "ActionType", "UtilityName", "UtilityParams"],
        values: [actionName, "RunUtility", utilityName, utilityParams]
    };
}

/**
 * Creates a workflow item data structure for Geocode Table action
 *
 * @param {string} actionName - The name of the action
 * @param {string} modelName - The model name
 * @param {string} tableName - The table name (customers, facilities, suppliers)
 * @returns {object} Object with keys and values arrays
 */
export function createGeocodeTableWorkflowItem(actionName, modelName, tableName) {
    return {
        keys: ["ActionName", "ActionType", "ModelName", "TableName"],
        values: [actionName, "GeocodeTable", modelName, tableName]
    };
}

/**
 * Creates a workflow item data structure for Import Data action
 *
 * @param {string} actionName - The name of the action
 * @param {string} sheetName - The Excel sheet name
 * @param {string} modelName - The model name
 * @param {string} tableName - The Cosmic Frog table name
 * @param {string} upsertOrReplace - Either "Upsert" or "Replace"
 * @returns {object} Object with keys and values arrays
 */
export function createImportDataWorkflowItem(actionName, sheetName, modelName, tableName, upsertOrReplace) {
    return {
        keys: ["ActionName", "ActionType", "SheetName", "ModelName", "CosmicFrogTable", "UpsertOrReplace"],
        values: [actionName, "ImportData", sheetName, modelName, tableName, upsertOrReplace]
    };
}

/**
 * Creates a workflow item data structure for Export Data action (new version with model and condition)
 *
 * @param {string} actionName - The name of the action
 * @param {string} modelName - The model name
 * @param {string} tableName - The Cosmic Frog table name
 * @param {string} sheetName - The Excel sheet name
 * @param {string} condition - The condition for export (optional)
 * @returns {object} Object with keys and values arrays
 */
export function createExportDataNewWorkflowItem(actionName, modelName, tableName, sheetName, condition) {
    return {
        keys: ["ActionName", "ActionType", "ModelName", "CosmicFrogTable", "SheetName", "Condition"],
        values: [actionName, "ExportData", modelName, tableName, sheetName, condition]
    };
}

/**
 * Creates a workflow item data structure for Upload File action
 *
 * @param {string} actionName - The name of the action
 * @param {string} sheetName - The Excel sheet name
 * @param {string} fileName - The file name to upload to platform
 * @returns {object} Object with keys and values arrays
 */
export function createUploadFileWorkflowItem(actionName, sheetName, fileName) {
    return {
        keys: ["ActionName", "ActionType", "SheetName", "FileName"],
        values: [actionName, "UploadFile", sheetName, fileName]
    };
}

/**
 * Creates a workflow item data structure for Download File action
 *
 * @param {string} actionName - The name of the action
 * @param {string} fileName - The file name to download from platform
 * @param {string} sheetName - The Excel sheet name
 * @returns {object} Object with keys and values arrays
 */
export function createDownloadFileWorkflowItem(actionName, fileName, sheetName) {
    return {
        keys: ["ActionName", "ActionType", "FileName", "SheetName"],
        values: [actionName, "DownloadFile", fileName, sheetName]
    };
}

export function createRunDataStarMacroWorkflowItem(actionName, projectName, macroName) {
    return {
        keys: ["ActionName", "ActionType", "ProjectName", "MacroName"],
        values: [actionName, "RunDataStar", projectName, macroName]
    }
}